import { createSlice } from '@reduxjs/toolkit';
import {
  configInterface,
  configInterfaceKeyNames,
  pagesInterface,
} from '../interfaces';
import { PriceView } from '../hooks/useGetGrossPrice';
import update from 'immutability-helper';
import { forEach } from 'lodash';
import { moduleExistsInPages, moduleExistsInLayouts, deleteModuleLogic } from '@skygroup/shared/utils/siteEngineHelpers';
import { ensureModulesObjects, removeModuleFromModulesArray, reorderModulesArray } from '@skygroup/shared/utils/modulesConversion';
// import moment from 'moment';

const params = new URL(window.location.href).searchParams;
const rtlLanguages = ['he_IL','ar_SA'];
let initLang = 'he_IL';

const pagesWithSamePath = (
  path1: string | string[],
  path2: string | string[]
): boolean => {
  if (typeof path1 == 'string' && typeof path2 == 'string') {
    return path1 === path2;
  }
  if (
    path1?.length !== path2?.length ||
    !Array.isArray(path1) ||
    !Array.isArray(path2)
  ) {
    return false;
  }
  return path1.sort().join() === path2.sort().join();
};


const initialState: stateInterface = {
  config: {} as configInterface,
  translations: {},
  agentInfo: {},
  diamondsWallet: undefined,
  currentLang: initLang,
  direction: rtlLanguages.includes(initLang) ? 'rtl' : 'ltr',
  isLayoutModalOpen: null,
  // remove: isUpgradePlanModalOpen
  isUpgradePlanModalOpen: false,
  isOtpAuthCheckModalOpen: false,
  membershipUser: null,

};
interface MembershipUserType {
  data: {
    TZ: string;
    Phone: string;
    Org_Code: string;
    Org_Name: string;
    Shave_Eligibility: string;
    Error_Message: string;
    logo: any
  };
  user_name: string;
}

interface stateInterface {
  config: configInterface;
  translations: any;
  agentInfo: any;
  diamondsWallet?: any;
  currentLang: any;
  direction: "ltr" | "rtl";
  isLayoutModalOpen: null | {
    type: 'update-card' | 'upgrade-plan' ;
    data?: any;
  };
  // remove: isUpgradePlanModalOpen
  isUpgradePlanModalOpen: boolean;
  isOtpAuthCheckModalOpen: boolean;
  membershipUser: MembershipUserType | null;
}

export const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    setIsUpgradePlanModalOpen: (state, action) => {
      state.isLayoutModalOpen = action.payload;
      // remove: isUpgradePlanModalOpen
      state.isUpgradePlanModalOpen = action.payload;
    },
    setIsOtpAuthCheckModalOpen: (state, action) => {
      state.isOtpAuthCheckModalOpen = action.payload;
    },
    updateConfig: (state, action) => {
      state.config = action.payload;

      // alway set the agency leading as top ags
      if (state.agentInfo?.agency?.secret) {
        state.config.siteConfig.ags = state.agentInfo.agency.secret;
      }
    },
    updateLang: (state, action) => {
      state.currentLang = action.payload;
      state.direction = rtlLanguages.includes(action.payload) ? 'rtl' : 'ltr';
    },
    updateTranslations: (state, action) => {
      state.translations = action.payload;
    },
    updateAgentInfo: (state, action) => {
      state.agentInfo = action.payload;

      // alway set the agency leading as top ags
      if (state.config?.siteConfig?.ags && state.agentInfo?.agency?.secret) {
        state.config.siteConfig.ags = state.agentInfo.agency.secret;
      }
    },
    setDiamondsWallet: (state, action) => {
      state.diamondsWallet = action.payload;
    },
    updateAgentPriceView: (state, action) => {
      const priceView = action.payload.isGrossPrice
        ? PriceView.Gross
        : PriceView.Net;
      localStorage.setItem('price_view', JSON.stringify(priceView));

      state.agentInfo.agent.price_view = priceView;
    },
    // updateModuleTheme: (state: any, action) => {
    //   const { moduleName, theme } = action.payload;
    //   state.config.modulesConfigs[moduleName].theme = theme;
    // },
    // updateSiteTheme: (state, action) => {
    //   const { theme } = action.payload;
    //   state.config.theme = theme;
    // },
    // updateLayout: (state, action) => {
    //   const { layouts } = action.payload;
    //   state.config.layouts = layouts;
    // },

    updateLayoutModulesIndexes: (state, action) => {
      const { dragIndex, hoverIndex, layoutName, layoutSection } =
        action.payload;
      const prevCards = state.config.layouts[layoutName][layoutSection];
      const updatedLayout = update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex]],
        ],
      });
      const layouts = {
        ...state.config.layouts,
        [layoutName]: {
          ...state.config.layouts[layoutName],
          [layoutSection]: updatedLayout,
        },
      };
      state.config.layouts = layouts;
    },
    setMembershipUser: (state, action) => {
      state.membershipUser = action.payload;
    },
    deleteLayoutModules: (state, action) => {
      const { moduleToDelete, layoutName, layoutSection } = action.payload;
      const layouts = {
        ...state.config.layouts,
        [layoutName]: {
          ...state.config.layouts[layoutName],
          [layoutSection]: state.config.layouts[layoutName][
            layoutSection
          ].filter((item: string) => item !== moduleToDelete),
        },
      };
      state.config.layouts = layouts;

      // DELETE MODULE FROM modulesConfigs if it is not used on any pages:
      let isInstanceInUse =
        moduleExistsInPages(state.config.pages, moduleToDelete) ||
        moduleExistsInLayouts(state.config.layouts, moduleToDelete);
      if (!isInstanceInUse) {
        // // IF CONDITION IS MET USE deleteModuleFromConfigs logic
        deleteModuleLogic(state, moduleToDelete);
      }
    },

    updateModuleIndex: (state, action) => {
      const { dragIndex, hoverIndex, pageId } = action.payload;

      if (!Array.isArray(state.config?.pages)) return state; // guard

      const currPages = [...state.config.pages];

      const updatedPages = currPages.map((page) => {
        if (!page) return page;

        // match by id or name as in your original code
        const matches = page.id === pageId || page.name === pageId;
        if (!matches) return page;

        // call the improved reorder which excludes isDeleted when computing indexes
        const pageModulesInUpdatedOrder = reorderModulesArray(
          page.modules || [],
          dragIndex,
          hoverIndex
        );

        return {
          ...page,
          modules: pageModulesInUpdatedOrder,
        };
      });

      state.config.pages = updatedPages;
    },

deleteModuleFromPage: (state, action) => {
  debugger;
  const { instance, pageId, moduleName } = action.payload;

  // defensive guards
  if (!instance) return;

  const isDup = typeof instance === 'string' && instance.endsWith('_dup');

  // Helper to normalize module objects
  const normalize = (arr:any) => ensureModulesObjects(arr || []);

  // DUP CASE: update module object in the page (remove overrideModuleName + mark deleted),
  // and then unconditionally remove the dup modulesConfigs entry.
  if (isDup) {
    const currPages = [...state.config.pages];
    const updatedPages = currPages.map((page) => {
      if (!page) return page;
      if (page.id === pageId || page.name === pageId) {
        const objs = normalize(page.modules || []);
        // find the module entry that has overrideModuleName === instance
        const idx = objs.findIndex((m) => m?.overrideModuleName === instance);
        if (idx >= 0) {
          const copy = [...objs];
          const modified = { ...(copy[idx] || {}) };
          // Remove overrideModuleName and mark deleted
          if ('overrideModuleName' in modified) delete modified.overrideModuleName;
          modified.isDeleted = true;
          // keep rest of meta intact (sort_index, module, etc.)
          copy[idx] = modified;
          return { ...page, modules: copy };
        }
        // fallback: nothing matched -> return page unchanged
        return page;
      }
      return page;
    });

    // apply pages update
    state.config.pages = updatedPages;

    // Unconditionally drop dup's config from modulesConfigs
    if (state.config.modulesConfigs && Object.prototype.hasOwnProperty.call(state.config.modulesConfigs, instance)) {
      delete state.config.modulesConfigs[instance];
    }

    // done: do not continue with normal deletion flow
    return;
  }

  // NON-DUP (original behavior) ----------------------------------------------------------------
  const currPages = [...state.config.pages];
  const updatedPages = currPages.map((page) => {
    if (page.id === pageId || page.name === pageId) {
      return {
        ...page,
        modules: removeModuleFromModulesArray(page.modules, instance),
      };
    }
    return page;
  });

  state.config.pages = updatedPages;

  // DELETE MODULE FROM modulesConfigs if it is not used on any pages or layouts:
  let isInstanceInUse =
    moduleExistsInPages(state.config.pages, instance) ||
    moduleExistsInLayouts(state.config.layouts, instance);

  if (!isInstanceInUse) {
    // original helper that cleans up modulesConfigs (keeps any other logic inside that helper)
    deleteModuleLogic(state, instance, moduleName);
  }
},


      deleteModuleFromConfigs: (state, action) => {
          const { instance, moduleName } = action.payload;
          deleteModuleLogic(state, instance, moduleName);
          // const { instance, moduleName } = action.payload;
          // if (moduleName && instance === moduleName) {
          //   return;
          // }

          // const currModuleConfigs = { ...state.config.modulesConfigs };
          // delete currModuleConfigs[instance]; // Remove the key
          // state.config.modulesConfigs = currModuleConfigs;
      },
  },
});

export const {
  setIsOtpAuthCheckModalOpen,
  setIsUpgradePlanModalOpen,
  updateConfig,
  updateTranslations,
  updateAgentInfo,
  setDiamondsWallet,
  updateAgentPriceView,
  // updateModuleTheme,
  // updateSiteTheme,
  // updateLayout,
  updateLayoutModulesIndexes,
  deleteLayoutModules,
  updateLang,
  setMembershipUser,
  updateModuleIndex,
  deleteModuleFromPage,
  deleteModuleFromConfigs,
} = configSlice.actions;

export default configSlice.reducer;
